import { cn } from "@/utils/cn";

/**
 * Card-style checkbox used for multi-select preference questions.
 * Selected state shows a gradient checkbox; unselected shows an empty square.
 */
export const CardCheckbox = ({
  selected,
  onToggle,
  title,
  description,
  disabled,
}: {
  selected: boolean;
  onToggle: () => void;
  title: string;
  description?: string;
  disabled?: boolean;
}) => (
  <div
    onClick={disabled ? undefined : onToggle}
    className={cn(
      "relative rounded-[14px] px-5 py-[18px] flex items-start gap-[14px] cursor-pointer transition-colors",
      selected
        ? "bg-preference-card-selected-bg border-2 border-preference-card-selected-border"
        : "bg-preference-card-unselected-bg border border-preference-card-unselected-border",
      disabled && !selected && "opacity-50 cursor-not-allowed",
    )}
  >
    {/* Checkbox indicator */}
    <div
      className={cn(
        "flex-shrink-0 w-5 h-5 rounded-[4px] flex items-center justify-center transition-colors mt-[1px]",
        selected
          ? "bg-preference-card-checkbox-selected"
          : "bg-theme-bg-subtle border-2 border-preference-card-unselected-border",
      )}
    >
      {selected && (
        <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
          <path
            d="M1 4L4 7L10 1"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
    {/* Label */}
    <div className="flex flex-col gap-1 flex-1 min-w-0">
      <p className="text-14 font-semibold text-preference-card-title leading-5">{title}</p>
      {description && (
        <p className="text-12 font-medium text-accent-250 leading-4">{description}</p>
      )}
    </div>
  </div>
);

/**
 * Card-style radio button used for single-select preference questions (e.g. Yes / No).
 * Selected state shows a filled purple radio dot with purple outline card.
 */
export const RadioCard = ({
  selected,
  onSelect,
  label,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
}) => (
  <div
    onClick={onSelect}
    className={cn(
      "flex items-center gap-3 rounded-[14px] px-[18px] py-[14px] cursor-pointer transition-colors",
      selected
        ? "bg-preference-card-selected-bg border-2 border-preference-card-selected-border"
        : "bg-preference-card-unselected-bg border border-preference-card-unselected-border",
    )}
  >
    {/* Radio dot */}
    <div
      className={cn(
        "w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-colors",
        selected ? "border-primary-purple" : "border-preference-card-unselected-border",
      )}
    >
      {selected && <div className="w-[10px] h-[10px] rounded-full bg-primary-purple" />}
    </div>
    <span className="text-14 font-medium text-preference-card-title leading-5">{label}</span>
  </div>
);
